import { parseApiError } from '@shared/lib/api-error';
import { isPhoneValid, toE164 } from '@shared/lib/phone';
import { PhoneInput } from '@shared/ui/PhoneInput';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Button, Drawer, Form, Select, Space, Typography, notification } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AdminUserResponse } from '@api/generated/types.gen';

import { createUserWithRoles, fetchAdminRoles, findUserByPhone, replaceUserRoles } from '../api';
import { mergeRoleIds } from '../lib';

const { Text } = Typography;

export const ADMIN_ROLES_KEY = ['admin-roles', 'admin-access'] as const;

type FormValues = {
  phone: string;
  roleIds: string[];
};

type SubmitResult = {
  mode: 'created' | 'attached';
  user: AdminUserResponse;
};

type AddAdminDrawerProps = {
  open: boolean;
  onClose: () => void;
  onDone: (result: SubmitResult) => void;
};

/**
 * Drawer «Новый администратор»: телефон + роли с правом `admin.access`.
 *
 * Двухфазный сабмит, чтобы не поднять до админа чужого пользователя молча:
 * 1. по телефону ищем существующего (`GET /api/admin/users?phone=`);
 *    нет — сразу `POST /api/admin/users` с выбранными ролями;
 * 2. есть — показываем предупреждение с его текущими ролями, кнопка меняется
 *    на «Назначить роли»; повторный сабмит делает `PUT .../roles` с объединением
 *    текущих и выбранных ролей (см. `mergeRoleIds`).
 *
 * Смена телефона в форме сбрасывает найденного пользователя.
 */
export function AddAdminDrawer({ open, onClose, onDone }: AddAdminDrawerProps) {
  const { t } = useTranslation(['admins', 'common', 'errors']);
  const [form] = Form.useForm<FormValues>();
  const [existing, setExisting] = useState<AdminUserResponse | null>(null);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setExisting(null);
    }
  }, [open, form]);

  const rolesQuery = useQuery({ queryKey: ADMIN_ROLES_KEY, queryFn: fetchAdminRoles, enabled: open });
  const roleOptions = (rolesQuery.data ?? []).map((r) => ({
    value: r.id ?? '',
    label: r.name ?? '',
  }));
  const noAdminRoles = rolesQuery.isSuccess && roleOptions.length === 0;

  const mutation = useMutation({
    mutationFn: async (values: FormValues): Promise<SubmitResult | null> => {
      const phoneE164 = toE164(values.phone);
      if (!phoneE164) throw new Error('INVALID_PHONE');

      if (existing?.phone === phoneE164) {
        const merged = mergeRoleIds(
          (existing.roles ?? []).map((r) => r.id ?? ''),
          values.roleIds,
        );
        const user = await replaceUserRoles(existing.id ?? '', merged);
        return { mode: 'attached', user };
      }

      const found = await findUserByPhone(phoneE164);
      if (found) {
        setExisting(found);
        return null;
      }
      const user = await createUserWithRoles(phoneE164, values.roleIds);
      return { mode: 'created', user };
    },
    onSuccess: (result) => {
      if (result) onDone(result);
    },
    onError: (err: unknown) => {
      const apiErr = parseApiError(
        (err as { response?: { data?: unknown } }).response?.data,
        (err as { response?: { status?: number } }).response?.status,
      );
      notification.error({
        message: t(`errors:${apiErr.code}`, { defaultValue: t('errors:UNKNOWN_ERROR') }),
        placement: 'topRight',
      });
    },
  });

  const existingRoleNames = (existing?.roles ?? [])
    .map((r) => r.name ?? '')
    .filter(Boolean)
    .join(', ');

  return (
    <Drawer
      title={t('admins:drawer.title')}
      placement="right"
      width="min(440px, 100vw)"
      open={open}
      onClose={onClose}
      destroyOnHidden
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>{t('common:actions.cancel')}</Button>
          <Button
            type="primary"
            loading={mutation.isPending}
            disabled={noAdminRoles}
            onClick={() => {
              form.submit();
            }}
          >
            {existing ? t('admins:drawer.submitAttach') : t('admins:drawer.submitCreate')}
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {t('admins:drawer.intro')}
        </Text>

        {noAdminRoles && <Alert type="warning" showIcon message={t('admins:drawer.noAdminRoles')} />}

        {existing && (
          <Alert
            type="info"
            showIcon
            message={t('admins:drawer.existingTitle')}
            description={t('admins:drawer.existingDescription', {
              phone: existing.phone,
              roles: existingRoleNames || t('admins:drawer.existingNoRoles'),
            })}
          />
        )}

        <Form<FormValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={(values) => {
            mutation.mutate(values);
          }}
          onValuesChange={(changed: Partial<FormValues>) => {
            if (changed.phone !== undefined) setExisting(null);
          }}
        >
          <Form.Item
            label={t('admins:drawer.phoneLabel')}
            name="phone"
            rules={[
              { required: true, message: t('errors:fields.NOT_BLANK') },
              {
                validator: (_, value: string) => {
                  if (value && !isPhoneValid(value)) {
                    return Promise.reject(new Error(t('errors:INVALID_PHONE')));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <PhoneInput placeholder={t('admins:drawer.phonePlaceholder')} autoFocus />
          </Form.Item>

          <Form.Item
            label={t('admins:drawer.rolesLabel')}
            name="roleIds"
            rules={[{ required: true, message: t('admins:drawer.rolesRequired') }]}
            extra={t('admins:drawer.rolesHelp')}
          >
            <Select
              mode="multiple"
              placeholder={t('admins:drawer.rolesPlaceholder')}
              options={roleOptions}
              loading={rolesQuery.isLoading}
              optionFilterProp="label"
            />
          </Form.Item>
        </Form>
      </Space>
    </Drawer>
  );
}
